import axios, {
  type AxiosRequestConfig,
  AxiosError,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import { useAuthStore } from '../stores/authStore';
import type { ErrorState } from '../types/error-types';
import type { IHttpClient, IHttpResponse } from './HttpClient';
import { environment, isDevelopment } from './environment';
import { getErrorMessage, getNetworkErrorMessage, getTimeoutErrorMessage } from './error-messages';

// Interfaz para la respuesta de error de tu API
interface APIErrorResponse {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  // [key: string]: any;
}

type QueryParams = Record<string, string | number | boolean | Array<string | number | boolean>>;

function serializeParams(params: QueryParams): string {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((v) => searchParams.append(key, String(v)));
    } else {
      searchParams.append(key, String(value));
    }
  });
  return searchParams.toString();
}

export function createAxiosHttpClient(): IHttpClient {
  const axiosInstance = axios.create({
    baseURL: environment.apiUrl,
    paramsSerializer: serializeParams,
  });

  // Aquí guardamos la función que nos da el token
  let getToken: (() => string | null) | null = null;

  // Variable global para almacenar la función showError del contexto
  // Se inicializará desde el componente principal
  let globalShowError: ((error: ErrorState) => void) | null = null;

  const setTokenProvider = (fn: () => string | null) => {
    getToken = fn;
  };

  const setErrorHandler = (fn: (error: ErrorState) => void) => {
    globalShowError = fn;
  };

  // Interceptors
  axiosInstance.interceptors.request.use(
    (request: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
      if (!request.url) return request;
      if (getToken) {
        const token = getToken();
        if (token) {
          request.headers.Authorization = `Bearer ${token}`;
        }
      }
      return request;
    },
    (error: AxiosError) => Promise.reject(error),
  );

  // Public auth endpoints handle their own error UI inline.
  // Authenticated auth endpoints (sign-out, change-password) use global toasts.
  const excludedPaths: string[] = [
    '/admin/auth/sign-in',
    '/admin/auth/request-password-reset',
    '/admin/auth/validate-password-reset-token',
    '/admin/auth/reset-password',
  ];

  axiosInstance.interceptors.response.use(
    (response: AxiosResponse): AxiosResponse => response,
    async (error: AxiosError<APIErrorResponse>) => {
      const url = error.config?.url ?? '';

      if (excludedPaths.some((path) => url.includes(path))) {
        return Promise.reject(error); // 👈 no mostrar modal
      }

      if (error.response?.status === 401) {
        const { isAuthenticated, logout, setErrorLogout } = useAuthStore.getState();
        if (isAuthenticated) {
          setErrorLogout('Tu sesión ha expirado por inactividad.');
          logout();
        }
        return Promise.reject(error);
      }

      // 🔹 Manejo de errores global
      if (!globalShowError) {
        console.error('Error handler not configured:', error);
        return Promise.reject(error);
      }

      let errorState: ErrorState;

      // Caso 1: Error de red
      if (!error.response) {
        if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
          const timeoutError = getTimeoutErrorMessage();
          errorState = {
            ...timeoutError,
            id: `error-${Date.now()}`,
            timestamp: new Date(),
            stack: isDevelopment() ? error.stack : undefined,
          };
        } else {
          const networkError = getNetworkErrorMessage();
          errorState = {
            ...networkError,
            id: `error-${Date.now()}`,
            timestamp: new Date(),
            stack: isDevelopment() ? error.stack : undefined,
          };
        }
      } else {
        // Caso 2: Error con respuesta del servidor
        const { data, status } = error.response;
        console.log('API error response data:', data);
        const errorMessage = getErrorMessage(data?.title, status, data?.detail);

        errorState = {
          ...errorMessage,
          id: `error-${Date.now()}`,
          timestamp: new Date(),
          statusCode: status,
          errorCode: data?.title,
          stack: isDevelopment() ? error.stack : undefined,
        };

        // Si es 401 pero no entra en el flujo de refresh → logout directo
        if (status === 401) {
          // localStorage.removeItem('auth_token');
          // window.location.href = '/login';
        }
      }

      globalShowError(errorState);
      return Promise.reject(error);
    },
  );

  // Métodos HTTP
  const get = async <T>(url: string, config?: AxiosRequestConfig): Promise<IHttpResponse<T>> => {
    const response = await axiosInstance.get<T>(url, config);
    return { data: response.data, status: response.status };
  };

  const post = async <T, R>(
    url: string,
    data: T,
    config?: AxiosRequestConfig,
  ): Promise<IHttpResponse<R>> => {
    const response = await axiosInstance.post<R>(url, data, config);
    return { data: response.data, status: response.status };
  };

  async function postPublic<T, R>(
    url: string,
    data: T,
    config?: AxiosRequestConfig,
  ): Promise<IHttpResponse<R>> {
    const response = await axiosInstance.post<R>(url, data, {
      ...config,
      headers: {
        ...config?.headers,
        // Aquí no se agrega Authorization
        'Content-Type': 'application/json',
      },
    });
    return { data: response.data, status: response.status };
  }

  const put = async <T>(
    url: string,
    data?: T,
    config?: AxiosRequestConfig,
  ): Promise<IHttpResponse<T>> => {
    const response = await axiosInstance.put<T>(url, data, config);
    return { data: response.data, status: response.status };
  };

  const patch = async <T>(
    url: string,
    data: T,
    config?: AxiosRequestConfig,
  ): Promise<IHttpResponse<T>> => {
    const response = await axiosInstance.patch<T>(url, data, config);
    return { data: response.data, status: response.status };
  };

  const remove = async <T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<IHttpResponse<T>> => {
    const response = await axiosInstance.delete<T>(url, { ...config, data });
    return { data: response.data, status: response.status };
  };

  return { get, post, postPublic, put, patch, delete: remove, setTokenProvider, setErrorHandler };
}
